/**
 * Pricing Page Component
 *
 * Displays the Armenian Link pricing tiers (Free, Plus, Pro) with feature
 * comparisons, FAQs, and call-to-action sections. Designed for a
 * non-technical audience with clear, jargon-free messaging.
 *
 * @module pages/pricing
 *
 * @remarks
 * - Three pricing tiers: Free ($0/forever), Plus ($60/year billed annually), Pro ($120/year billed annually)
 * - Feature comparison with visual checkmarks
 * - FAQ section addressing common questions
 * - Responsive grid layout for pricing cards
 * - Authenticated users see different CTA links (billing vs login)
 *
 * @example
 * // Route definition in App.tsx
 * <Route path="/pricing" element={<PricingPage />} />
 */

import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Check, X, Zap, BarChart3, Shield, ArrowRight } from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { useAuth } from '@/client/hooks/use-auth';
import { useReducedMotion } from '@/client/hooks/use-reduced-motion';
import { getEnabledServices, type ServiceDefinition } from '@/client/lib/config';

// =============================================================================
// Feature List Item
// =============================================================================

function FeatureItem({
  children,
  included = true,
}: {
  children: React.ReactNode;
  included?: boolean;
}) {
  return (
    <li className={cn('flex items-start gap-3', !included && 'opacity-60')}>
      <div
        className={cn(
          'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
          included ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
        )}
      >
        {included ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
      </div>
      <span
        className={cn(
          'text-sm',
          included ? 'text-foreground/80' : 'text-muted-foreground line-through'
        )}
      >
        {children}
      </span>
    </li>
  );
}

// =============================================================================
// Pricing Card
// =============================================================================

interface PricingCardProps {
  name: string;
  description: string;
  price: string;
  period: string;
  features: { text: string; included: boolean }[];
  cta: string;
  ctaLink: string;
  ctaLinkAuth?: string; // Link to use when user is authenticated
  popular?: boolean;
  delay?: number;
  hasTrial?: boolean; // Show trial badge and messaging
}

function PricingCard({
  name,
  description,
  price,
  period,
  features,
  cta,
  ctaLink,
  ctaLinkAuth,
  popular = false,
  delay = 0,
  hasTrial = false,
}: PricingCardProps) {
  const { isAuthenticated } = useAuth();
  const prefersReducedMotion = useReducedMotion();
  const targetLink = isAuthenticated && ctaLinkAuth ? ctaLinkAuth : ctaLink;

  return (
    <motion.div
      initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: prefersReducedMotion ? 0 : delay,
        duration: prefersReducedMotion ? 0.01 : 0.5,
      }}
      className={cn(
        'relative rounded-2xl p-8',
        popular
          ? 'bg-gradient-to-b from-amber-500/10 to-orange-500/5 border-2 border-amber-500/30'
          : 'bg-card border border-border'
      )}
    >
      {/* Badges row - both positioned together to prevent overlap */}
      {(popular || hasTrial) && (
        <div className="absolute -top-3 left-0 right-0 flex justify-center gap-2">
          {popular && (
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-zinc-900 text-xs font-bold px-3 py-1 rounded-full">
              BEST VALUE
            </span>
          )}
          {hasTrial && (
            <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              14-DAY FREE TRIAL
            </span>
          )}
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-xl font-bold text-foreground mb-2">{name}</h3>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold text-foreground">{price}</span>
          <span className="text-muted-foreground">/{period}</span>
        </div>
        {name === 'Plus' && (
          <p className="text-sm text-amber-500 mt-1">That&apos;s just 🖐️ $5/month!</p>
        )}
        {name === 'Pro' && (
          <p className="text-sm text-amber-500 mt-1">That&apos;s just $10/month!</p>
        )}
      </div>

      <ul className="space-y-3 mb-8">
        {features.map((feature, i) => (
          <FeatureItem key={i} included={feature.included}>
            {feature.text}
          </FeatureItem>
        ))}
      </ul>

      <Link
        to={targetLink}
        className={cn(
          'w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold transition-all',
          popular
            ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-zinc-900'
            : 'bg-muted hover:bg-muted/80 text-foreground'
        )}
      >
        {cta}
        <ArrowRight className="w-4 h-4" />
      </Link>
    </motion.div>
  );
}

// =============================================================================
// FAQ Item
// =============================================================================

function FAQItem({ question, answer }: { question: string; answer: string }) {
  return (
    <div className="border-b border-border pb-6">
      <h4 className="text-foreground font-medium mb-2">{question}</h4>
      <p className="text-muted-foreground text-sm leading-relaxed">{answer}</p>
    </div>
  );
}

// =============================================================================
// Pricing Page
// =============================================================================

export function PricingPage() {
  // NOTE: This page now uses semantic colors from the theme system.
  // The decorative gradients use subtle amber/orange accents that work in both light and dark modes.
  const prefersReducedMotion = useReducedMotion();
  const freeTierFeatures = [
    { text: 'Unlimited short links', included: true },
    { text: 'QR code generation', included: true },
    { text: 'Basic landing pages', included: true },
    { text: 'Link click tracking', included: true },
    { text: '30-day analytics history', included: true },
    { text: 'Vanity URLs', included: false },
    { text: 'Forms & event signups', included: false },
  ];

  const plusTierFeatures = [
    { text: 'Everything in Free', included: true },
    { text: 'Forms & event signups', included: true },
    { text: 'Custom landing pages', included: true },
    { text: '1-year analytics history', included: true },
    { text: 'Vanity URLs (custom short codes)', included: true },
    { text: 'Link folders for organization', included: true },
    { text: 'Share analytics with team (25 shares)', included: true },
    { text: 'Branded QR codes (remove our logo)', included: true },
  ];

  const proTierFeatures = [
    { text: 'Everything in Plus', included: true },
    { text: 'Unlimited forms & pages', included: true },
    { text: 'Custom link previews', included: true },
    { text: 'Unlimited analytics history', included: true },
    { text: 'Unlimited team shares', included: true },
    { text: 'API access', included: true },
    { text: 'Team members (up to 10)', included: true },
    { text: 'Webhook notifications', included: true },
    { text: 'Priority support', included: true },
  ];

  const faqs = [
    {
      question: 'Can I use Armenian Link for free?',
      answer:
        'Yes! You can create unlimited short links, generate QR codes, and track basic analytics completely free. No account required. No credit card required.',
    },
    {
      question: 'What happens to my links if I cancel Plus?',
      answer:
        "Your links will continue to work forever. You'll just lose access to advanced analytics, vanity URLs, and API features. We never delete your links.",
    },
    {
      question: 'Is there a limit on link clicks?',
      answer:
        'No limits on clicks for any plan. Your short links can receive unlimited traffic at no extra cost.',
    },
    {
      question: 'Can I upgrade or downgrade anytime?',
      answer:
        "Absolutely. Upgrade to Plus anytime and get instant access to all features. Downgrade whenever you want - we'll prorate any remaining time.",
    },
    {
      question: 'Do you offer refunds?',
      answer:
        "Yes, we offer a 30-day money-back guarantee. If it's not right for you, just email us for a full refund. No questions asked!",
    },
    {
      question: 'Why is it so affordable?',
      answer:
        "We built Armenian Link to serve our community. Our Plus and Pro plans simply cover our hosting and development costs. We're not trying to maximize profit - just sustainability.",
    },
    {
      question: 'Why build this for the Armenian community specifically?',
      answer:
        "Big link management platforms are built for enterprise marketing teams with dedicated budgets. Our churches, cultural organizations, and local businesses need the same tools but at accessible prices. We built Armenian Link to give our community professional-grade link analytics without the professional-grade price tag. It's about empowerment, not profit.",
    },
  ];

  return (
    <div className="bg-background min-h-screen">
      {/* Decorative background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl" />
      </div>

      {/* Hero */}
      <section className="relative pt-20 pb-16 lg:pt-32 lg:pb-24">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-600 text-sm font-medium mb-6"
          >
            <Zap className="w-4 h-4" />
            Simple, transparent pricing
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: prefersReducedMotion ? 0 : 0.1,
              duration: prefersReducedMotion ? 0.01 : 0.6,
            }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6"
          >
            Free forever.
            <br />
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
              Plus from $5/month. Pro from $10/month.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: prefersReducedMotion ? 0 : 0.2,
              duration: prefersReducedMotion ? 0.01 : 0.6,
            }}
            className="text-xl text-muted-foreground max-w-2xl mx-auto"
          >
            Start shortening links instantly. No hidden fees, no surprises.
          </motion.p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="relative pb-24">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <PricingCard
              name="Free"
              description="Perfect for personal use"
              price="$0"
              period="forever"
              features={freeTierFeatures}
              cta="Get Started Free"
              ctaLink="/"
              delay={0.3}
            />
            <PricingCard
              name="Plus"
              description="For power users who want full control ✨"
              price="$60"
              period="year"
              features={plusTierFeatures}
              cta="Get Plus"
              ctaLink="/login"
              ctaLinkAuth="/settings/billing"
              popular
              hasTrial
              delay={0.4}
            />
            <PricingCard
              name="Pro"
              description="For teams and advanced features"
              price="$120"
              period="year"
              features={proTierFeatures}
              cta="Get Pro"
              ctaLink="/login"
              ctaLinkAuth="/settings/billing?tier=pro"
              hasTrial
              delay={0.5}
            />
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="relative py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything you need to
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                {' '}
                own your links
              </span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Both plans include our core features. Plus unlocks the full potential.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Dynamic services from config */}
            {getEnabledServices().map((service: ServiceDefinition) => (
              <div key={service.id} className="flex items-start gap-4 p-4">
                <div
                  className={`w-10 h-10 rounded-lg bg-gradient-to-br ${service.gradient.replace('from-', 'from-').replace('to-', 'to-')}/20 flex items-center justify-center flex-shrink-0`}
                >
                  <service.icon className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-foreground font-medium mb-1">{service.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {service.shortName === service.name
                      ? 'Click tracking'
                      : service.description.split('.')[0]}
                  </p>
                </div>
              </div>
            ))}
            {/* Always show Analytics and Privacy */}
            <div className="flex items-start gap-4 p-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-foreground font-medium mb-1">Analytics</h3>
                <p className="text-sm text-muted-foreground">Track every click</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-foreground font-medium mb-1">Privacy First</h3>
                <p className="text-sm text-muted-foreground">No tracking cookies</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Frequently asked questions
            </h2>
            <p className="text-muted-foreground">Everything you need to know about Armenian Link</p>
          </div>

          <div className="max-w-2xl mx-auto space-y-6">
            {faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="container mx-auto px-4">
          <div className="relative max-w-3xl mx-auto text-center">
            <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 rounded-3xl blur-2xl" />
            <div className="relative bg-card backdrop-blur-xl rounded-2xl border border-border p-12 shadow-lg">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ready to start shortening?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
                Join Armenian churches, cultural centers, nonprofits, and businesses using Armenian
                Link to understand their audience and grow their impact.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/#try-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-8 py-4 text-lg font-semibold text-zinc-900 transition-all hover:scale-105"
                >
                  Create Your First Link
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
